import React, { FC, memo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import classnames from "classnames";
import {
  emitLiveBarItemClicked,
  getTextValue,
} from "chat/analytics/emitLiveBarItemClicked";
import { ReactComponent as LiveBadge } from "chat/assets/liveBadge.svg";
import { ReactComponent as PremiumBadge } from "chat/assets/premiumBadge.svg";
import { chatSelectors } from "chat/exports/state/selectors";
import { EventFields } from "chat/imports/analytics";
import { Avatar } from "chat/imports/components";
import { useVipInfo } from "chat/imports/hooks";
import { RootState, settingsSelectors } from "chat/imports/state";
import {
  AvatarSize,
  CustomMode,
  LiveBarStreamDetail,
} from "chat/imports/types";
import {
  getPremiumEnabled,
  getStreamMaxModerationLevelFullAccess,
  makeLinkToLiveStream,
} from "chat/imports/utils";
import styles from "./LiveBarCarouselItem.scss";

interface LiveBarCarouselItemProps extends LiveBarStreamDetail {
  index: number;
  streamsList: string[];
}

const selector = (
  state: RootState,
  moderationLevel: number,
  accountId?: string
) => ({
  accountInfo: chatSelectors.getConversationAccountInfoByAccountId(
    state,
    accountId
  ),
  isStreamNotAllowed:
    getStreamMaxModerationLevelFullAccess(state) < (moderationLevel ?? 0),
  isAccessToPremiumEnabled: Boolean(
    Number(settingsSelectors.isAccessToPremiumEnabled(state))
  ),
  isPremiumEnabled: getPremiumEnabled(state),
});

const LiveBarCarouselItem: FC<LiveBarCarouselItemProps> = ({
  anchor,
  stream,
  moderationLevel,
  streamsList,
  index,
}) => {
  const {
    accountInfo,
    isStreamNotAllowed,
    isAccessToPremiumEnabled,
    isPremiumEnabled,
  } = useSelector(
    (state: RootState) =>
      selector(state, moderationLevel || 0, anchor?.encryptedAccountId),
    shallowEqual
  );

  const history = useHistory();
  const { vipLabel } = useVipInfo({
    vipLevel: accountInfo?.account_info?.vip_level,
  });

  const handleStreamClick = () => {
    const streamLink = makeLinkToLiveStream(stream?.id);

    emitLiveBarItemClicked({
      streamId: stream?.id,
      peerId: anchor?.encryptedAccountId || "",
      position: index + 1,
      text: getTextValue({
        streamKind: stream?.streamKind,
        isAccessToPremiumEnabled,
        isPremiumEnabled,
      }),
    });

    history.push(streamLink, {
      preparedStreamIds: streamsList,
      initialStreamId: stream?.id,
      [EventFields.STREAM_MODE]: CustomMode.PLAYLIST,
    });
  };

  return (
    <div
      className={classnames(styles.root)}
      onClick={handleStreamClick}
      role="button"
      tabIndex={0}
    >
      {!isStreamNotAllowed &&
        (stream?.streamKind === 2 ? (
          <LiveBadge
            className={styles.avatarLiveBadge}
            data-testid="avatar-live-label"
          />
        ) : (
          isAccessToPremiumEnabled &&
          isPremiumEnabled && (
            <PremiumBadge
              className={styles.avatarLiveBadge}
              data-testid="avatar-premium-label"
            />
          )
        ))}

      <Avatar
        imageUrl={anchor?.profilePictureUrl}
        className={styles.avatar}
        size={AvatarSize.SMALL_PLUS}
        data-testid="live-bar-carousel-item-avatar"
        vipLabel={vipLabel}
      />

      <div className={styles.userName}>{anchor?.firstName}</div>
    </div>
  );
};

export default memo(LiveBarCarouselItem);
